/**
 * Журнал интеграции с Mindbox.
 *
 * События уходят в Mindbox только через наш бэкенд (`lib/mindboxBackend`).
 * Интеграцию отлаживают на собранных стендах, где `import.meta.env.DEV` уже
 * false, поэтому журнал включён по умолчанию и пишет не только то, что мы
 * отправили бэкенду, но и то, что он ответил.
 *
 * Выключить: `VITE_MINDBOX_DEBUG=false` на сборке или
 * `localStorage.setItem('mindbox-debug', '0')` в консоли стенда.
 */

const STORAGE_KEY = 'mindbox-debug';
const HISTORY_LIMIT = 200;

export type MindboxLogKind = 'init' | 'send' | 'ok' | 'error' | 'skip' | 'info';

export type MindboxLogEntry = {
  at: string;
  kind: MindboxLogKind;
  message: string;
  operation?: string;
  payload?: unknown;
  details?: unknown;
};

const env = import.meta.env as unknown as Record<string, string | undefined>;

const history: MindboxLogEntry[] = [];

const readStoredFlag = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const enabled = readStoredFlag() !== '0' && env.VITE_MINDBOX_DEBUG !== 'false';

const MARK: Record<MindboxLogKind, string> = {
  init: '⚙',
  send: '→',
  ok: '✓',
  error: '✕',
  skip: '∅',
  info: 'i',
};

/** JSON без исключений: в payload может приехать циклическая ссылка. */
const safeJson = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
};

export const mindboxLog = (entry: Omit<MindboxLogEntry, 'at'>) => {
  const record: MindboxLogEntry = { at: new Date().toISOString(), ...entry };

  // История пишется всегда, даже с выключенным выводом: если проблему заметили
  // уже после того, как событие ушло, getMindboxHistory() её ещё помнит.
  history.push(record);
  if (history.length > HISTORY_LIMIT) history.shift();

  if (!enabled) return record;

  const title = `[mindbox] ${MARK[entry.kind]} ${entry.operation ?? ''} ${entry.message}`
    .replace(/\s+/g, ' ')
    .trim();

  const write =
    entry.kind === 'error'
      ? console.error
      : entry.kind === 'skip'
        ? console.warn
        : console.log;

  if (entry.payload === undefined) {
    write(title);
  } else {
    // Объектом — чтобы развернуть в vConsole, строкой — чтобы скопировать
    // ровно то, что уехало, и приложить к тикету в поддержку Mindbox.
    write(title, entry.payload);
    write(`[mindbox] json ${entry.operation ?? ''}`.trim(), safeJson(entry.payload));
  }

  if (entry.details !== undefined) {
    write('[mindbox] details', entry.details);
  }

  return record;
};

export const getMindboxHistory = () => [...history];

